import type { GeoJSONSource } from 'mapbox-gl';
import mapboxgl from 'mapbox-gl';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMapbox } from '@/contexts/MapboxContext';
import { useCdcVesselHistory } from '../hooks/useCdcVessels';
import { useCdcStore } from '../stores/useCdcStore';
import type { CdcVesselPosition } from '../types';

// Layer IDs
const CDC_TRAIL_SOURCE = 'cdc-animation-trail-source';
const CDC_TRAIL_LAYER = 'cdc-animation-trail';
const CDC_PLAYED_SOURCE = 'cdc-animation-played-source';
const CDC_PLAYED_GLOW = 'cdc-animation-played-glow';
const CDC_PLAYED_LAYER = 'cdc-animation-played';
const CDC_GAPS_SOURCE = 'cdc-animation-gaps-source';
const CDC_GAPS_LAYER = 'cdc-animation-gaps';

// Time range options
const TIME_RANGES = [
  { label: '6h', value: 6 },
  { label: '12h', value: 12 },
  { label: '24h', value: 24 },
  { label: '3d', value: 72 },
  { label: '7d', value: 168 },
];

const SPEED_OPTIONS = [0.5, 1, 2, 4];

/**
 * Get rotation angle from position data
 */
function getRotationAngle(position?: CdcVesselPosition): number {
  if (!position) return 0;
  if (position.heading !== undefined && position.heading !== null && position.heading !== 511) {
    return position.heading;
  }
  if (position.cog !== undefined && position.cog !== null) {
    return position.cog;
  }
  return 0;
}

/**
 * CdcAnimationOverlay - Floating glassmorphic animation controls
 * Renders at bottom center of map during vessel history playback
 */
export const CdcAnimationOverlay: React.FC = () => {
  const { map, isMapReady } = useMapbox();
  const animatingMmsi = useCdcStore((state) => state.animatingMmsi);
  const isOverlayOpen = useCdcStore((state) => state.isAnimationOverlayOpen);
  const durationHours = useCdcStore((state) => state.animationDurationHours);
  const setDurationHours = useCdcStore((state) => state.setAnimationDurationHours);
  const stopAnimation = useCdcStore((state) => state.stopAnimation);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [progress, setProgress] = useState(0);
  const [layersAdded, setLayersAdded] = useState(false);

  const animationRef = useRef<number | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const lastTimeRef = useRef<number>(0);

  // Fetch history data
  const { data: historyData, isLoading } = useCdcVesselHistory(
    animatingMmsi,
    durationHours,
    !!animatingMmsi && isOverlayOpen,
  );

  const positions = historyData?.positions || [];
  const trail = historyData?.trail;

  // Clean up animation
  const cleanup = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    if (map.current) {
      const m = map.current;
      try {
        if (m.getLayer(CDC_PLAYED_GLOW)) m.removeLayer(CDC_PLAYED_GLOW);
        if (m.getLayer(CDC_PLAYED_LAYER)) m.removeLayer(CDC_PLAYED_LAYER);
        if (m.getLayer(CDC_TRAIL_LAYER)) m.removeLayer(CDC_TRAIL_LAYER);
        if (m.getLayer(CDC_GAPS_LAYER)) m.removeLayer(CDC_GAPS_LAYER);
        if (m.getSource(CDC_PLAYED_SOURCE)) m.removeSource(CDC_PLAYED_SOURCE);
        if (m.getSource(CDC_TRAIL_SOURCE)) m.removeSource(CDC_TRAIL_SOURCE);
        if (m.getSource(CDC_GAPS_SOURCE)) m.removeSource(CDC_GAPS_SOURCE);
      } catch {
        // Map may be destroyed
      }
    }
    setLayersAdded(false);
    setProgress(0);
    setIsPlaying(false);
  }, [map]);

  // Cleanup on unmount or when animation stops
  useEffect(() => {
    if (!isOverlayOpen) {
      cleanup();
    }
    return () => cleanup();
  }, [cleanup, isOverlayOpen]);

  // Initialize layers when history data is available
  useEffect(() => {
    if (!isMapReady || !map.current || !trail || !animatingMmsi || !isOverlayOpen || layersAdded) return;

    const m = map.current;

    // Add trail source and layer (full path - dashed gray)
    if (!m.getSource(CDC_TRAIL_SOURCE)) {
      m.addSource(CDC_TRAIL_SOURCE, { type: 'geojson', data: trail });
    }

    if (!m.getLayer(CDC_TRAIL_LAYER)) {
      m.addLayer({
        id: CDC_TRAIL_LAYER,
        type: 'line',
        source: CDC_TRAIL_SOURCE,
        paint: {
          'line-color': '#666666',
          'line-width': 2,
          'line-opacity': 0.5,
          'line-dasharray': [2, 4],
        },
      });
    }

    // Add played trail source
    if (!m.getSource(CDC_PLAYED_SOURCE)) {
      m.addSource(CDC_PLAYED_SOURCE, {
        type: 'geojson',
        data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [] }, properties: {} },
      });
    }

    // Add glow layer
    if (!m.getLayer(CDC_PLAYED_GLOW)) {
      m.addLayer({
        id: CDC_PLAYED_GLOW,
        type: 'line',
        source: CDC_PLAYED_SOURCE,
        paint: {
          'line-color': '#00ffaa',
          'line-width': 12,
          'line-opacity': 0.3,
          'line-blur': 4,
        },
      });
    }

    // Add played trail layer
    if (!m.getLayer(CDC_PLAYED_LAYER)) {
      m.addLayer({
        id: CDC_PLAYED_LAYER,
        type: 'line',
        source: CDC_PLAYED_SOURCE,
        paint: {
          'line-color': '#00ff88',
          'line-width': 4,
          'line-opacity': 1,
        },
      });
    }

    // Add gaps layer
    if (historyData?.gaps && historyData.gaps.features.length > 0) {
      if (!m.getSource(CDC_GAPS_SOURCE)) {
        m.addSource(CDC_GAPS_SOURCE, { type: 'geojson', data: historyData.gaps });
      }
      if (!m.getLayer(CDC_GAPS_LAYER)) {
        m.addLayer({
          id: CDC_GAPS_LAYER,
          type: 'line',
          source: CDC_GAPS_SOURCE,
          paint: {
            'line-color': '#ff6b6b',
            'line-width': 2,
            'line-opacity': 0.6,
            'line-dasharray': [4, 6],
          },
        });
      }
    }

    // Create animated marker
    const markerEl = document.createElement('div');
    markerEl.className = 'cdc-animation-marker';
    const initialHeading = getRotationAngle(positions[0]);
    markerEl.innerHTML = `
      <div class="cdc-marker-container" style="width: 32px; height: 32px; position: relative;">
        <div class="cdc-marker-pulse" style="position: absolute; width: 100%; height: 100%; border-radius: 50%; background: rgba(0, 255, 136, 0.2); animation: cdc-pulse 1.5s ease-in-out infinite;"></div>
        <div class="cdc-marker-arrow" style="
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-bottom: 20px solid #00ff88;
          transform: translate(-50%, -50%) rotate(${initialHeading}deg);
          filter: drop-shadow(0 0 4px rgba(0, 255, 136, 0.6));
        "></div>
      </div>
    `;

    // Add CSS animation
    if (!document.getElementById('cdc-animation-styles')) {
      const style = document.createElement('style');
      style.id = 'cdc-animation-styles';
      style.textContent = `
        @keyframes cdc-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.5); opacity: 0.4; }
        }
        .cdc-marker-arrow { transition: transform 0.1s ease-out; }
      `;
      document.head.appendChild(style);
    }

    if (positions.length > 0) {
      const firstPos = positions[0];
      markerRef.current = new mapboxgl.Marker({ element: markerEl }).setLngLat([firstPos.lon, firstPos.lat]).addTo(m);

      // Fit map to trail bounds
      const coords = positions.map((p) => [p.lon, p.lat] as [number, number]);
      const bounds = coords.reduce((b, coord) => b.extend(coord), new mapboxgl.LngLatBounds(coords[0], coords[0]));
      m.fitBounds(bounds, { padding: 100 });
    }

    setLayersAdded(true);
  }, [isMapReady, map, trail, animatingMmsi, isOverlayOpen, layersAdded, positions, historyData?.gaps]);

  // Animation loop
  useEffect(() => {
    if (!isPlaying || !layersAdded || positions.length < 2) return;

    const animate = (timestamp: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      const duration = 30000 / playbackSpeed;
      const increment = (delta / duration) * 100;

      setProgress((prev) => {
        const next = prev + increment;
        if (next >= 100) {
          setIsPlaying(false);
          return 100;
        }
        return next;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    lastTimeRef.current = 0;
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, layersAdded, positions.length, playbackSpeed]);

  // Update map based on progress + lazy camera follow
  useEffect(() => {
    if (!map.current || !layersAdded || positions.length < 2) return;

    const m = map.current;
    const idx = Math.floor((progress / 100) * (positions.length - 1));
    const currentPos = positions[Math.min(idx, positions.length - 1)];

    // Update marker position and rotation
    if (markerRef.current && currentPos) {
      markerRef.current.setLngLat([currentPos.lon, currentPos.lat]);

      const rotation = getRotationAngle(currentPos);
      const markerEl = markerRef.current.getElement();
      const arrowEl = markerEl?.querySelector('.cdc-marker-arrow') as HTMLElement | null;
      if (arrowEl) {
        arrowEl.style.transform = `translate(-50%, -50%) rotate(${rotation}deg)`;
      }

      // Continuous camera follow - smoothly center on vessel
      if (isPlaying) {
        m.easeTo({
          center: [currentPos.lon, currentPos.lat],
          duration: 100, // Short duration for smooth continuous follow
          easing: (t) => t, // Linear easing for smooth motion
        });
      }
    }

    // Update played trail
    const playedCoords = positions.slice(0, idx + 1).map((p) => [p.lon, p.lat]);
    const source = m.getSource(CDC_PLAYED_SOURCE) as GeoJSONSource | undefined;
    if (source && playedCoords.length > 1) {
      source.setData({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: playedCoords },
        properties: {},
      });
    }
  }, [progress, map, layersAdded, positions, isPlaying]);

  // Don't render if overlay is not open
  if (!isOverlayOpen || !animatingMmsi) return null;

  const handleClose = () => {
    cleanup();
    stopAnimation();
  };

  const handlePlayPause = () => {
    if (progress >= 100) setProgress(0);
    setIsPlaying(!isPlaying);
  };

  const handleSpeedChange = () => {
    const currentIdx = SPEED_OPTIONS.indexOf(playbackSpeed);
    const nextIdx = (currentIdx + 1) % SPEED_OPTIONS.length;
    setPlaybackSpeed(SPEED_OPTIONS[nextIdx]);
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProgress(Number(e.target.value));
    setIsPlaying(false);
  };

  const handleTimeRangeChange = (hours: number) => {
    setDurationHours(hours);
    cleanup();
    setLayersAdded(false);
  };

  const currentIdx = Math.floor((progress / 100) * (positions.length - 1));
  const currentPos = positions[Math.min(currentIdx, positions.length - 1)];
  const currentTime = currentPos?.ts
    ? (() => { const d = new Date(currentPos.ts); const day = d.getUTCDate(); const m = d.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' }); const y = d.getUTCFullYear(); const t = d.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'UTC', hour12: false }); const s = day === 1 || day === 21 || day === 31 ? 'st' : day === 2 || day === 22 ? 'nd' : day === 3 || day === 23 ? 'rd' : 'th'; return `${m} ${day}${s}, ${y}, ${t} UTC`; })()
    : '';

  return (
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 pointer-events-auto">
      {/* Panel-styled container matching sidebar */}
      <div className="bg-panel-surface backdrop-blur-xl border border-panel-border rounded-2xl shadow-2xl overflow-hidden min-w-[400px]">
        {/* Header with vessel name */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-panel-border">
          <div className="flex items-center gap-2">
            <span className="text-panel-primary text-sm">●</span>
            <span className="text-white font-medium text-sm">
              {isLoading ? 'Loading...' : historyData?.name || animatingMmsi}
            </span>
          </div>
          <button type="button" onClick={handleClose} className="text-gray-400 hover:text-white transition-colors p-1">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              role="img"
              aria-label="Close"
            >
              <title>Close</title>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Time range pills */}
        <div className="flex items-center justify-center gap-1 px-4 py-3 border-b border-panel-border">
          {TIME_RANGES.map((range) => (
            <button
              key={range.value}
              type="button"
              onClick={() => handleTimeRangeChange(range.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                durationHours === range.value
                  ? 'bg-panel-primary text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>

        {/* No history message */}
        {!isLoading && positions.length === 0 && (
          <div className="px-4 py-6 text-center">
            <p className="text-yellow-400 text-sm">No position history available</p>
            <p className="text-gray-500 text-xs mt-1">Try a longer time range</p>
          </div>
        )}

        {/* Controls - only show if we have positions */}
        {!isLoading && positions.length > 0 && (
          <>
            {/* Timeline scrubber */}
            <div className="px-4 py-3">
              <input
                type="range"
                min="0"
                max="100"
                value={progress}
                onChange={handleProgressChange}
                className="w-full h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:w-4
                  [&::-webkit-slider-thumb]:h-4
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:bg-panel-primary
                  [&::-webkit-slider-thumb]:shadow-lg
                  [&::-webkit-slider-thumb]:cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-2">
                <span>{currentTime}</span>
                <span>{Math.round(progress)}%</span>
              </div>
            </div>

            {/* Playback controls */}
            <div className="flex items-center justify-center gap-4 px-4 py-4">
              {/* Skip back */}
              <button
                type="button"
                onClick={() => setProgress(Math.max(0, progress - 10))}
                className="text-gray-400 hover:text-white transition-colors p-2"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" role="img" aria-label="Skip back">
                  <title>Skip back</title>
                  <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z" />
                </svg>
              </button>

              {/* Play/Pause */}
              <button
                type="button"
                onClick={handlePlayPause}
                className="w-14 h-14 rounded-full bg-panel-primary hover:bg-panel-primary-400 text-white flex items-center justify-center transition-colors shadow-lg"
              >
                {isPlaying ? (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" role="img" aria-label="Pause">
                    <title>Pause</title>
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24" role="img" aria-label="Play">
                    <title>Play</title>
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Skip forward */}
              <button
                type="button"
                onClick={() => setProgress(Math.min(100, progress + 10))}
                className="text-gray-400 hover:text-white transition-colors p-2"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" role="img" aria-label="Skip forward">
                  <title>Skip forward</title>
                  <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z" />
                </svg>
              </button>

              {/* Speed */}
              <button
                type="button"
                onClick={handleSpeedChange}
                className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm text-white font-medium transition-colors"
              >
                {playbackSpeed}x
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
